import { BaseChart, ChartMouseHandler, useChart } from '@/components/ECharts';
import { HighlightLegend } from '@/components/ECharts/HightlightLegend';
import {
    EChartsOption,
    ResizedHandler,
    SelectedChangeHandler,
} from '@/components/ECharts/useChart';
import { cn } from '@/utils';
import { useConfigContext } from '@/context/configContext';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { ContributionAndSpendByPeriod } from '@/dashboards/types';
import { FEATURE_FLAG_SUBJECTS } from '@/features/Casl';
import {
    transformWeeklyToMonthlyContributionAndSpend,
    transformWeeklyToMonthlyRecord,
} from '@/utils/weeklyToMonthlyUtils';
import { BarSeriesOption, LineSeriesOption } from 'echarts';
import { FC, memo, useCallback, useMemo } from 'react';
import moment from 'moment/moment';
import { renderTooltip } from './tooltips/renderTooltip';

const currencyFormat = Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
    notation: 'compact',
});

const formatAxisDate = (value: string, period?: 'weekly' | 'monthly'): string => {
    if (!value) return '';

    if (period === 'weekly') {
        const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (dayMatch) {
            const m = moment.utc(value, 'YYYY-MM-DD');
            return `${m.isoWeek()}-${m.isoWeekYear()}`;
        }
        return value;
    }

    const monthMatch = /^(\d{4})-(\d{2})$/.exec(value);
    if (monthMatch) {
        return `${monthMatch[2]}-${monthMatch[1]}`;
    }

    const dayMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dayMatch) {
        return `${dayMatch[2]}-${dayMatch[1]}`;
    }

    return value;
};

type SeriesOption = LineSeriesOption | BarSeriesOption;

type ChartOptions = EChartsOption<SeriesOption>;

const getEChartsOptions = ({
    seriesData,
    xAxisData,
    totalContributionByDate,
    optTotalContributionByDate,
    optSeriesData,
    optXAxisData,
    kpiFormatter,
    isKpiLineEnabled = true,
    period = 'monthly',
}: {
    xAxisData: string[];
    optXAxisData: string[];
    seriesData: ContributionAndSpendByPeriod;
    optSeriesData: ContributionAndSpendByPeriod;
    totalContributionByDate: Record<string, number>;
    optTotalContributionByDate: Record<string, number>;
    kpiFormatter: (value: string | undefined | number) => string;
    isKpiLineEnabled: boolean;
    period?: 'weekly' | 'monthly';
}): ChartOptions => {
    // Use utility for both previous and optimized data
    const prev = transformWeeklyToMonthlyContributionAndSpend(
        xAxisData,
        seriesData,
        period,
    );
    const opt = transformWeeklyToMonthlyContributionAndSpend(
        optXAxisData,
        optSeriesData,
        period,
    );

    const aggregatedTotalContributionByDate = transformWeeklyToMonthlyRecord(
        xAxisData,
        totalContributionByDate,
        period,
    );

    const aggregatedOptTotalContributionByDate = transformWeeklyToMonthlyRecord(
        optXAxisData,
        optTotalContributionByDate,
        period,
    );

    const emptyData = Object.keys(prev.seriesData).length
        ? new Array(3).fill('')
        : [];

    const dates = [...prev.xAxisData, ...emptyData, ...opt.xAxisData];

    const historicalDataSeries: ChartOptions['series'] = Object.entries(
        prev.seriesData,
    ).map(([name, { data, color }], index) => {
        return {
            name,
            type: 'bar',
            stack: 'Total',
            symbolSize: 2,
            showSymbol: false,
            showAllSymbol: false,
            triggerLineEvent: true,
            color: color,
            barMaxWidth: 50,
            itemStyle: {
                borderRadius:
                    index === Object.keys(prev.seriesData).length - 1
                        ? [5, 5, 0, 0]
                        : 0,
            },
            tooltip: {
                valueFormatter: (value) => currencyFormat.format(Number(value)),
            },
            data: Object.entries(data).map(([name, { spend }]) => ({
                name,
                value: spend,
            })),
        } as unknown as ChartOptions['series'][0];
    });

    const blankSpace = Object.keys(prev.seriesData).length
        ? new Array(prev.xAxisData.length ? prev.xAxisData.length + 3 : 0).fill(
              {},
          )
        : [];

    const projectedOptDataSeries: ChartOptions['series'] = Object.entries(
        opt.seriesData,
    ).map(([name, { data, color }], index) => {
        return {
            name,
            type: 'bar',
            stack: 'Total',
            symbolSize: 2,
            showSymbol: false,
            showAllSymbol: false,
            triggerLineEvent: true,
            color: color,
            barMaxWidth: 50,
            itemStyle: {
                borderRadius:
                    index === Object.keys(opt.seriesData).length - 1
                        ? [5, 5, 0, 0]
                        : 0,
            },
            data: [
                ...blankSpace,
                ...Object.entries(data).map(([period, { spend }]) => {
                    return {
                        name: period,
                        value: spend,
                        isOptimized: true,
                    };
                }),
            ],
        } as unknown as ChartOptions['series'][0];
    });

    const historicalKpiSeries: ChartOptions['series'] = isKpiLineEnabled
        ? [
              {
                  id: 'kpi',
                  color: '#FFF',
                  name: 'KPI',
                  type: 'line',
                  yAxisIndex: 1,
                  lineStyle: {
                      width: 3,
                  },
                  triggerLineEvent: true,
                  data: [
                      ...Object.entries(aggregatedTotalContributionByDate).map(
                          ([period, value]) => {
                              return {
                                  name: period,
                                  value,
                                  isOptimized: false,
                              };
                          },
                      ),
                  ],
              },
          ]
        : [];

    const projectedKpiSeries: ChartOptions['series'] =
        isKpiLineEnabled
            ? [
                  {
                      id: 'kpi-projected',
                      color: '#FFF',
                      name: 'KPI',
                      type: 'line',
                      yAxisIndex: 1,
                      lineStyle: {
                          width: 3,
                      },
                      triggerLineEvent: true,
                      data: [
                          ...new Array(blankSpace.length).fill(null),
                          ...Object.entries(
                              aggregatedOptTotalContributionByDate,
                          ).map(([period, value]) => {
                              return { name: period, value, isOptimized: true };
                          }),
                      ],
                  },
              ]
            : [];

    return {
        animation: true,
        tooltip: {
            show: false,
            trigger: 'axis',
            triggerOn: 'mousemove|click',
            backgroundColor: 'transparent',
            borderWidth: 0,
            padding: 0,
            type: 'item',
            confine: true,
            extraCssText: 'max-width: 600px; white-space: pre-wrap;',
            formatter: '',
        },
        legend: {
            show: true,
            showCustom: true,
        },
        grid: {
            top: '5%',
            left: '4%',
            right: '4%',
            bottom: 0,
            containLabel: true,
        },
        xAxis: [
            {
                type: 'category',
                boundaryGap: true,
                data: dates,
                axisLabel: {
                    show: true,
                    rotate: 90,
                    formatter: (value: string) => formatAxisDate(value, period),
                },
                axisLine: {
                    lineStyle: {
                        opacity: 0.3,
                    },
                },
            },
        ],
        yAxis: [
            {
                type: 'value',
                name: 'Media Investment',
                nameLocation: 'middle',
                nameGap: 50,
                nameTextStyle: {
                    fontSize: '0.875rem',
                },
                axisLabel: {
                    formatter: (value: number) =>
                        currencyFormat.format(Number(value)),
                },
                axisLine: {
                    show: false,
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        opacity: 0.2,
                    },
                },
            },
            {
                type: 'value',
                name: 'KPI',
                nameGap: 55,
                nameLocation: 'middle',
                nameTextStyle: {
                    fontSize: '0.875rem',
                },
                axisLabel: {
                    formatter: (value: number) => kpiFormatter(Number(value)),
                },
                axisLine: {
                    show: false,
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        opacity: 0.2,
                    },
                },
            },
        ],
        series: [
            ...historicalDataSeries,
            ...projectedOptDataSeries,
            ...historicalKpiSeries,
            ...projectedKpiSeries,
        ],
    };
};

export interface ContributionSeasonalityData {
    data: ContributionAndSpendByPeriod;
    dates: string[];
    totalContributionByDate: Record<string, number>;
}

interface ContributionSeasonalityProps {
    className?: string;
    isLoading: boolean;
    period?: 'weekly' | 'monthly';
    data: {
        previous: ContributionSeasonalityData;
        optimized: ContributionSeasonalityData;
    };
}

export const ContributionSeasonalityBarChart: FC<ContributionSeasonalityProps> =
    memo(({ className, isLoading, data, period = 'monthly' }) => {
        const { formatNumber } = useConfigContext();
        const {
            contributionFormatter,
            dashboard: { featureFlags },
        } = useDashboardGlobalContext();

        const isKpiLineEnabled: boolean =
            featureFlags.find(
                (flag) =>
                    flag.subject ===
                    FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_CONTRIBUTION_SEASONALITY_KPI_LINE_CHART,
            )?.isEnabled !== false;

        // Get the highest value of the optimized contribution bars / KPI line to position the title correctly
        const optimizedHighestValue = useMemo(() => {
            let highest = 0;
            let type = 'bar';
            if (data?.optimized?.data) {
                Object.values(data?.optimized?.data).reduce((acc, { data }) => {
                    Object.entries(data).map(([period, contribution]) => {
                        if (!acc[period]) {
                            acc[period] = 0;
                        }
                        acc[period] += contribution;
                        if (!highest || acc[period] > highest) {
                            highest = acc[period];
                        }
                    });
                    return acc;
                }, {});
                Object.values(data?.optimized?.totalContributionByDate).map(
                    (value) => {
                        if (value > highest) {
                            type = 'line';
                            highest = value;
                        }
                    },
                );
            }
            return { type, value: highest };
        }, [data]);

        const onResize: ResizedHandler = useCallback(
            (instance) => {
                if (data?.optimized?.dates) {
                    const firstOptimizedDate = data.optimized.dates[0];
                    const lastOptimizedDate =
                        data.optimized.dates[data.optimized.dates.length - 1];

                    if (!firstOptimizedDate || !lastOptimizedDate) {
                        return;
                    }

                    const firstOptimizedBarCoords = instance.convertToPixel(
                        optimizedHighestValue.type === 'line'
                            ? { seriesId: 'kpi-projected' }
                            : { seriesIndex: 0 },
                        [firstOptimizedDate, optimizedHighestValue.value],
                    );
                    const lastOptimizedBarCoords = instance.convertToPixel(
                        optimizedHighestValue.type === 'line'
                            ? { seriesId: 'kpi-projected' }
                            : { seriesIndex: 0 },
                        [lastOptimizedDate, optimizedHighestValue.value],
                    );

                    if (firstOptimizedBarCoords && lastOptimizedBarCoords) {
                        // Approximate Optimized contribution text width
                        const textWidth = 170;
                        // Leave some space between the title and the top of the chart
                        const topSpacing = 25;

                        const leftX = firstOptimizedBarCoords[0] || 0;
                        const rightX = lastOptimizedBarCoords[0] || 0;

                        const titleTopPosition =
                            (firstOptimizedBarCoords[1] || 0) - topSpacing;

                        // Width of the optimized contribution bars
                        const width = rightX - leftX;

                        // If the width of the bars is smaller than the text width, shift the text a bit
                        const offset =
                            width < textWidth ? (textWidth - width) / 2 : 0;

                        const titleLeftPosition = leftX - offset;

                        instance.setOption({
                            graphic: [
                                {
                                    type: 'text',
                                    z: 100,
                                    left: titleLeftPosition,
                                    top: titleTopPosition,
                                    style: {
                                        backgroundColor: 'transparent',
                                        fill: '#FFF',
                                        width: width,
                                        text: 'Optimized contribution',
                                        font: '0.8125rem DM Mono',
                                        textAlign: 'center',
                                    },
                                },
                            ],
                        });
                    }
                }
            },
            [data, optimizedHighestValue],
        );

        const onClick: ChartMouseHandler = useCallback(
            (context, { setSelected }) => {
                if (context.seriesName) {
                    setSelected((prev) => {
                        if (prev || prev === context.seriesName) {
                            return '';
                        }
                        return context.seriesName;
                    });
                } else {
                    setSelected('');
                }
            },
            [],
        );

        const onSelectedChange: SelectedChangeHandler<typeof eChartsOptions> =
            useCallback(
                ({ selected, instance, series }) => {
                    instance.setOption({
                        tooltip: {
                            show: true,
                            trigger: selected ? 'axis' : 'item',
                            formatter: renderTooltip({
                                selected,
                                formatNumber: (value) =>
                                    formatNumber(Number(value)),
                                kpiFormatter: (value) =>
                                    contributionFormatter(Number(value)),
                            }),
                        },
                        series: series.map((seriesOption) => {
                            const existingLineStyle =
                                (seriesOption as LineSeriesOption).lineStyle ||
                                {};
                            if (selected && selected !== seriesOption.name) {
                                return {
                                    ...seriesOption,
                                    markPoint: {
                                        itemStyle: {
                                            opacity: 0.1,
                                        },
                                    },
                                    itemStyle: { opacity: 0.1 },
                                    lineStyle: {
                                        ...existingLineStyle,
                                        opacity: 0.1,
                                    },
                                };
                            }
                            return {
                                ...seriesOption,
                                markPoint: {
                                    itemStyle: {
                                        opacity: 1,
                                    },
                                },
                                itemStyle: { opacity: 1 },
                                lineStyle: {
                                    ...existingLineStyle,
                                    opacity: 1,
                                },
                            };
                        }),
                    });
                },
                [formatNumber, contributionFormatter],
            );

        const eChartsOptions = useMemo(() => {
            return getEChartsOptions({
                seriesData: data?.previous?.data ?? {},
                xAxisData: data?.previous?.dates ?? [],
                totalContributionByDate:
                    data?.previous?.totalContributionByDate ?? {},
                optSeriesData: data?.optimized?.data ?? {},
                optXAxisData: data?.optimized?.dates ?? [],
                optTotalContributionByDate:
                    data?.optimized?.totalContributionByDate ?? {},
                kpiFormatter: (value) =>
                    contributionFormatter(Number(value), {
                        notation: 'compact',
                        maximumFractionDigits: 2,
                    }),
                isKpiLineEnabled,
                period,
            });
        }, [
            contributionFormatter,
            data?.optimized?.data,
            data?.optimized?.dates,
            data?.optimized?.totalContributionByDate,
            data?.previous?.data,
            data?.previous?.dates,
            data?.previous?.totalContributionByDate,
            isKpiLineEnabled,
            period,
        ]);

        const {
            ref: chartRef,
            chartOptions,
            onEvents,
            selected,
            setSelected,
            onChartReady,
        } = useChart({
            chartOptions: eChartsOptions,
            onResize,
            onSelectedChange,
            onClick,
        });

        // Series names are duplicated across the historical/optimized bar
        // series (and the KPI line appears twice), so dedupe by name to build a
        // single legend entry per category/variable.
        const legendItems = useMemo(() => {
            const seen = new Set<string>();
            return chartOptions.series.reduce<
                { name: string; highlighted: boolean; color?: string }[]
            >((acc, s) => {
                const name = String(s.name);
                if (!name || seen.has(name)) {
                    return acc;
                }
                seen.add(name);
                acc.push({ name, highlighted: true, color: s.color });
                return acc;
            }, []);
        }, [chartOptions.series]);

        if (isLoading) {
            return (
                <div className={className}>
                    <div className="flex items-center justify-center w-full h-full" />
                </div>
            );
        }

        return (
            <div className={cn(className, 'flex flex-col')}>
                <div className="relative w-full flex-1 min-h-0">
                    <BaseChart
                        $shouldExpand
                        className="pt-3"
                        ref={chartRef}
                        option={chartOptions}
                        onEvents={onEvents}
                        onChartReady={onChartReady}
                    />
                </div>
                <div className="relative z-20 px-4 py-2">
                    <HighlightLegend
                        highlighted={selected}
                        legendItems={legendItems}
                        onClick={(name) => {
                            setSelected((prev) => (prev === name ? '' : name));
                        }}
                    />
                </div>
            </div>
        );
    });

ContributionSeasonalityBarChart.displayName = 'ContributionSeasonalityBarChart';
